import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/AuthContext';
import { apiService } from '../services/api';

interface DashboardStats {
  totalTournaments: number;
  joinedTournaments: number;
  totalWinnings: number;
  currentRank: number;
  totalPoints: number;
  winRate: number;
}

interface RecentActivity {
  id: number;
  type: 'tournament_joined' | 'match_won' | 'prize_won' | 'ranking_changed';
  title: string;
  description: string;
  timestamp: string;
  amount?: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalTournaments: 0,
    joinedTournaments: 0,
    totalWinnings: 0,
    currentRank: 0,
    totalPoints: 0,
    winRate: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch tournaments
        const tournaments = await apiService.getTournaments();
        
        // Fetch user's teams
        const myTeams = await apiService.getMyTeams();
        
        // Fetch notifications for recent activity
        const notifications = await apiService.getNotifications();

        // Calculate stats from real data
        const calculatedStats: DashboardStats = {
          totalTournaments: tournaments.length || 0,
          joinedTournaments: myTeams.length || 0,
          totalWinnings: 0, // TODO: Calculate from actual winnings data
          currentRank: 0, // TODO: Calculate from standings
          totalPoints: 0, // TODO: Calculate from actual points
          winRate: 0, // TODO: Calculate from match results
        };

        // Convert notifications to recent activity format
        const activityFromNotifications: RecentActivity[] = notifications.slice(0, 5).map((notification: any, index: number) => ({
          id: index + 1,
          type: 'tournament_joined', // Default type, could be enhanced based on notification content
          title: notification.title || 'New Notification',
          description: notification.message || 'You have a new notification',
          timestamp: notification.created_at || new Date().toISOString(),
        }));

        setStats(calculatedStats);
        setRecentActivity(activityFromNotifications);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        
        // Fallback to mock data if API fails
        const mockStats: DashboardStats = {
          totalTournaments: 156,
          joinedTournaments: 12,
          totalWinnings: 8500,
          currentRank: 42,
          totalPoints: 2840,
          winRate: 68.5,
        };

        const mockActivity: RecentActivity[] = [
          {
            id: 1,
            type: 'tournament_joined',
            title: 'Joined IPL Fantasy League 2024',
            description: 'You successfully joined the tournament',
            timestamp: '2024-01-15T10:30:00Z',
          },
          {
            id: 2,
            type: 'match_won',
            title: 'Match Victory!',
            description: 'Won against Team Thunder in T20 Blast',
            timestamp: '2024-01-14T18:45:00Z',
            amount: 500,
          },
          {
            id: 3,
            type: 'prize_won',
            title: 'Prize Money Won',
            description: 'Received prize money for 3rd place finish',
            timestamp: '2024-01-12T14:20:00Z',
            amount: 1200,
          },
          {
            id: 4,
            type: 'ranking_changed',
            title: 'Ranking Improved',
            description: 'Moved up 5 positions in global rankings',
            timestamp: '2024-01-10T09:15:00Z',
          },
        ];

        setStats(mockStats);
        setRecentActivity(mockActivity);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'tournament_joined':
        return '🏏';
      case 'match_won':
        return '🏆';
      case 'prize_won':
        return '💰';
      case 'ranking_changed':
        return '📈';
      default:
        return '📋';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'tournament_joined':
        return 'text-secondary bg-secondary/10';
      case 'match_won':
        return 'text-accent bg-accent/10';
      case 'prize_won':
        return 'text-accent bg-accent/20';
      case 'ranking_changed':
        return 'text-primary bg-primary/10';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-2">⚠️</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Showing mock data as fallback</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-primary min-h-screen p-4">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-accent">
              Welcome back, {user?.display_name || 'Player'}! 
            </h1>
            <p className="text-accent text-3xl md:text-4xl font-bold mb-2 tracking-widest uppercase drop-shadow-lg" style={{ fontFamily: 'Comic Sans MS, cursive, sans-serif' }}>
              Fantasy Cricket re imagined.
            </p>
            <p className="text-blue-100">
              Ready to dominate the fantasy cricket world today?
            </p>
          </div>
          <div className="hidden md:block">
            <div className="text-6xl">🏏</div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tournaments</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalTournaments}</p>
            </div>
            <div className="text-3xl">🏏</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Joined Tournaments</p>
              <p className="text-3xl font-bold text-blue-600">{stats.joinedTournaments}</p>
            </div>
            <div className="text-3xl">🎯</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Winnings</p>
              <p className="text-3xl font-bold text-green-600">₹{stats.totalWinnings.toLocaleString()}</p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Current Rank</p>
              <p className="text-3xl font-bold text-purple-600">#{stats.currentRank}</p>
            </div>
            <div className="text-3xl">🏆</div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Overview</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Total Points</span>
                <span className="text-sm font-semibold text-gray-900">{stats.totalPoints}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${Math.min((stats.totalPoints / 5000) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Win Rate</span>
                <span className="text-sm font-semibold text-gray-900">{stats.winRate}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${stats.winRate}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.slice(0, 4).map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getActivityColor(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                  <p className="text-xs text-gray-500">{activity.description}</p>
                  <p className="text-xs text-gray-400">{formatDate(activity.timestamp)}</p>
                </div>
                {activity.amount && (
                  <div className="text-sm font-semibold text-green-600">
                    +₹{activity.amount}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors">
            <span>🏏</span>
            <span>Join Tournament</span>
          </button>
          <button className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors">
            <span>⚡</span>
            <span>Make Transfers</span>
          </button>
          <button className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors">
            <span>🎯</span>
            <span>View Standings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
